import { useRef, useState } from 'react';
import type { PlayerMessage } from '@shared/types.js';
import { extractMapsUrl } from '@shared/geo.js';
import { TerminalShell } from '../terminal/TerminalShell.js';
import { CameraCapture } from '../shared/CameraCapture.js';
import { LocationButton } from './LocationButton.js';
import { resolveImageUrl } from '@shared/image.js';
import { gameTimeZone } from '../../lib/gameTime.js';

function formatEntryTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('he-IL', {
      timeZone: gameTimeZone(),
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/**
 * יומן שדה — פאנל נפרד לגמרי מהמשימה (חלון משלו, לבקשת המשתמש), שבו הסוכן
 * יכול לכתוב פתקים חופשיים ולצרף תמונות של רמזים שמצא — צילום חי במצלמה או
 * העלאה מהגלריה. כל ההיסטוריה מוצגת בתוך אותו חלון כמו צ'אט עם עצמו.
 */
export function FieldNotes({
  messages,
  pendingCount,
  animationsEnabled,
  onSend,
}: {
  messages: PlayerMessage[];
  pendingCount: number;
  animationsEnabled: boolean;
  onSend: (text: string, imageBlob?: Blob | null) => Promise<{ ok: true; queued: boolean } | { ok: false; error: string }>;
}) {
  const [text, setText] = useState('');
  const [pendingImage, setPendingImage] = useState<{ blob: Blob; previewUrl: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const setImage = (blob: Blob) => {
    if (pendingImage) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage({ blob, previewUrl: URL.createObjectURL(blob) });
  };

  const clearImage = () => {
    if (pendingImage) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed && !pendingImage) return;
    setSending(true);
    setStatus(null);
    const result = await onSend(trimmed, pendingImage?.blob ?? null);
    setSending(false);
    if (!result.ok) {
      setStatus(`⚠️ ${result.error}`);
      return;
    }
    setText('');
    clearImage();
    setStatus(result.queued ? '⚠️ נשמר — יישלח כשהחיבור יחזור' : '✓ נשלח');
  };

  return (
    <TerminalShell
      animationsEnabled={animationsEnabled}
      ticker="◤ FIELD LOG ◢ AGENT NOTES ◢ ENCRYPTED STORAGE ◢"
      className="field-notes"
      header={
        <div className="term-panel-header">
          <span>יומן שדה</span>
          <span>{pendingCount > 0 ? `${pendingCount} ממתינות` : `${messages.length} פתקים`}</span>
        </div>
      }
    >
      {cameraOpen && (
        <CameraCapture
          onCapture={(blob) => {
            setImage(blob);
            setCameraOpen(false);
          }}
          onCancel={() => setCameraOpen(false)}
        />
      )}

      <div className="field-notes__log mono">
        {messages.length === 0 && <div className="empty-state">עדיין אין פתקים. כתבו כאן כל מה שגיליתם.</div>}
        {[...messages].reverse().map((m) => (
          <div
            key={m.id}
            className={`field-notes__entry ${m.sender === 'admin' ? 'field-notes__entry--admin' : ''}`}
          >
            <div className="field-notes__entry-time">
              {m.sender === 'admin' && <span className="field-notes__entry-from">מפקדה:</span>}
              {m.sender === 'admin' && m.urgent && <span className="field-notes__entry-urgent">🚨 דחוף</span>}
              {formatEntryTime(m.sentAt)}
            </div>
            {m.text && <div className="field-notes__entry-text">{m.text}</div>}
            {m.imageUrl && (
              <img src={resolveImageUrl(m.imageUrl)} alt="רמז מצורף" className="field-notes__entry-image" />
            )}
            {extractMapsUrl(m.text) && <LocationButton url={extractMapsUrl(m.text)!} />}
          </div>
        ))}
      </div>

      <div className="field-notes__composer">
        {pendingImage && (
          <div className="field-notes__preview">
            <img src={pendingImage.previewUrl} alt="תצוגה מקדימה" />
            <button type="button" className="field-notes__preview-remove" onClick={clearImage} aria-label="הסר תמונה">
              ✕
            </button>
          </div>
        )}
        <div className="field-notes__row">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="field-notes__file-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setImage(file);
            }}
          />
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setCameraOpen(true)}
            aria-label="צלם עכשיו"
            title="צלם עכשיו"
          >
            📸
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => fileInputRef.current?.click()}
            aria-label="בחר תמונה מהגלריה"
            title="בחר מהגלריה"
          >
            📷
          </button>
          <input
            type="text"
            className="field-notes__input"
            value={text}
            placeholder="כתבו פתק..."
            maxLength={2000}
            disabled={sending}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSend();
            }}
          />
          <button
            type="button"
            className="btn btn--sm btn--primary"
            disabled={sending || (!text.trim() && !pendingImage)}
            onClick={() => void handleSend()}
          >
            שלח
          </button>
        </div>
        {status && <div className="field-notes__status mono">{status}</div>}
      </div>
    </TerminalShell>
  );
}
